import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { getSlideId } from '../../utils/LearnTab/preview.utils';

export const useSlideNavigation = ({
    data,
    selectedSession = data?.selectedSession,
    setSelectedSession = data?.setSelectedSession,
    setSelectedSection = data?.setSelectedSection,
    setShowOutline,
    sessionSlides = data?.sessionSlides ?? {},
    sessionAssessments = data?.sessionAssessments ?? {},
    modulesData = data?.modulesData ?? [],
    contentStatus = data?.contentStatus ?? {},
    fetchSessionSlides = data?.fetchSessionSlides,
    openSessions = data?.openSessions ?? {},
    setOpenSessions = data?.setOpenSessions,
    setOpenModules = data?.setOpenModules,
    setOpenUnits = data?.setOpenUnits,
    isMobile = false,
    isCollapsed = false,
    setIsCollapsed,
    lastSpokenSlideTitleRef,
    isEditMode = false,
    enabled = true,
    landOnLastSlideRef: externalLandOnLastSlideRef,
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [currentSegment, setCurrentSegment] = useState(0);
    const [activeSegment, setActiveSegment] = useState(null);
    const [currentSegmentSlides, setCurrentSegmentSlides] = useState([]);
    const [segments, setSegments] = useState([]);

    const currentSlideRef = useRef(0);
    const lastActiveSegmentRef = useRef(null);
    const internalLandOnLastSlideRef = useRef(false);
    const landOnLastSlideRef = externalLandOnLastSlideRef || internalLandOnLastSlideRef;
    const preserveSlideIdRef = useRef(null);
    const preserveSlideIndexRef = useRef(null);
    const isLanguageTogglingRef = useRef(false);
    const isSavingRef = useRef(false);

    // Keep currentSlide in ref
    useEffect(() => {
        currentSlideRef.current = currentSlide;
    }, [currentSlide]);

    const currentSessionSlide = useMemo(() => (
        currentSegmentSlides && currentSegmentSlides.length > 0
            ? currentSegmentSlides[currentSlide]
            : null
    ), [currentSegmentSlides, currentSlide]);

    const [pollResponses, setPollResponses] = useState({});

    const recordPollSubmission = useCallback(({ slideId, selectedOption, feedback, isCorrect }) => {
        if (slideId) {
            setPollResponses(prev => ({
                ...prev,
                [slideId]: { selectedOption, feedback, isCorrect: Boolean(isCorrect) }
            }));
        }
    }, []);

    const currentPollFeedback = useMemo(() => {
        if (!currentSegmentSlides || currentSlide == null || currentSlide === 0) return null;
        const currentSlideObj = currentSegmentSlides[currentSlide];
        if (!currentSlideObj) return null;

        const origin = currentSlideObj._pbdq_origin || currentSlideObj;
        const slideType = (origin.slide_type || currentSlideObj._pbdq_kind || '').toLowerCase();
        const isReveal = slideType === 'reveal' || Boolean(origin.reveal_title || origin.correct_answer_text);

        if (!isReveal) return null;

        const prevSlide = currentSegmentSlides[currentSlide - 1];
        if (!prevSlide) return null;

        const prevSlideId = getSlideId(prevSlide);
        const recorded = prevSlideId ? pollResponses[prevSlideId] : null;

        if (recorded) {
            if (recorded.isCorrect) {
                return null;
            }
            const prevOrigin = prevSlide._pbdq_origin || prevSlide;
            const prevOptions = prevSlide.Options || prevOrigin.options || prevSlide.options || [];
            const chosenOpt = (typeof recorded.selectedOption === 'number' && prevOptions[recorded.selectedOption])
                ? prevOptions[recorded.selectedOption]
                : null;
            if (chosenOpt && (chosenOpt.is_correct === true || chosenOpt.is_correct === 'true')) {
                return null;
            }
            const dynamicFeedback = typeof chosenOpt === 'object' && chosenOpt ? chosenOpt.feedback_if_chosen : null;
            return dynamicFeedback || null;
        }

        return null;
    }, [currentSegmentSlides, currentSlide, pollResponses]);

    const isPollCorrect = useMemo(() => {
        if (!currentSegmentSlides || currentSlide == null || currentSlide === 0) return false;
        const currentSlideObj = currentSegmentSlides[currentSlide];
        if (!currentSlideObj) return false;

        const origin = currentSlideObj._pbdq_origin || currentSlideObj;
        const slideType = (origin.slide_type || currentSlideObj._pbdq_kind || '').toLowerCase();
        const isReveal = slideType === 'reveal' || Boolean(origin.reveal_title || origin.correct_answer_text);
        if (!isReveal) return false;

        const prevSlide = currentSegmentSlides[currentSlide - 1];
        if (!prevSlide) return false;

        const prevSlideId = getSlideId(prevSlide);
        const recorded = prevSlideId ? pollResponses[prevSlideId] : null;

        if (recorded) {
            if (recorded.isCorrect) return true;
            const prevOrigin = prevSlide._pbdq_origin || prevSlide;
            const prevOptions = prevSlide.Options || prevOrigin.options || prevSlide.options || [];
            const chosenOpt = (typeof recorded.selectedOption === 'number' && prevOptions[recorded.selectedOption])
                ? prevOptions[recorded.selectedOption]
                : null;
            if (chosenOpt && (chosenOpt.is_correct === true || chosenOpt.is_correct === 'true')) {
                return true;
            }
            return false;
        }

        return false;
    }, [currentSegmentSlides, currentSlide, pollResponses]);

    // Active segment / slides update effect
    useEffect(() => {
        if (activeSegment && (sessionSlides[activeSegment] || activeSegment === '__PRE_ASSESSMENT__' || activeSegment === '__POST_ASSESSMENT__')) {
            const isLanguageToggling = isLanguageTogglingRef.current;
            const isSaving = isSavingRef.current;
            const segmentChanged = activeSegment !== lastActiveSegmentRef.current;

            const shouldStartAtSlideZero = segmentChanged && !isLanguageToggling && !isSaving;
            if (shouldStartAtSlideZero) {
                preserveSlideIdRef.current = null;
                preserveSlideIndexRef.current = null;
            }

            if (!isLanguageToggling && !isSaving && lastSpokenSlideTitleRef) {
                lastSpokenSlideTitleRef.current = null;
            }

            const hasPreserveSlide = !shouldStartAtSlideZero && (preserveSlideIdRef.current != null || preserveSlideIndexRef.current != null);
            const landOnLastSlide = landOnLastSlideRef.current;
            landOnLastSlideRef.current = false;

            if (activeSegment === '__PRE_ASSESSMENT__' || activeSegment === '__POST_ASSESSMENT__') {
                const sessKey = selectedSession?.session_code;
                const assess = sessionAssessments?.[sessKey] || {};
                const assessmentItems = activeSegment === '__PRE_ASSESSMENT__' ? (assess.pre || []) : (assess.post || []);
                const assessmentSlides = assessmentItems.map((item, idx) => ({ kind: 'ASSESSMENT', questionIndex: idx }));
                setCurrentSegmentSlides(assessmentSlides);
                if (landOnLastSlide) {
                    setCurrentSlide(Math.max(assessmentSlides.length - 1, 0));
                } else if (shouldStartAtSlideZero) {
                    setCurrentSlide(0);
                } else if (hasPreserveSlide && preserveSlideIndexRef.current != null && assessmentSlides.length > 0) {
                    setCurrentSlide(Math.min(preserveSlideIndexRef.current, assessmentSlides.length - 1));
                } else if (!isLanguageToggling && !isSaving && segmentChanged) {
                    setCurrentSlide(0);
                }
            } else {
                const slides = Array.isArray(sessionSlides[activeSegment])
                    ? sessionSlides[activeSegment]
                    : [sessionSlides[activeSegment]];
                setCurrentSegmentSlides(slides);
                if (landOnLastSlide) {
                    setCurrentSlide(Math.max(slides.length - 1, 0));
                } else if (shouldStartAtSlideZero) {
                    setCurrentSlide(0);
                } else if (hasPreserveSlide && slides.length > 0) {
                    const slideId = preserveSlideIdRef.current;
                    const prevIndex = preserveSlideIndexRef.current != null ? preserveSlideIndexRef.current : 0;
                    if (slideId != null && getSlideId(slides[0]) != null) {
                        const idx = slides.findIndex((s) => getSlideId(s) === slideId);
                        if (idx >= 0) {
                            setCurrentSlide(idx);
                        } else {
                            setCurrentSlide(Math.min(prevIndex, slides.length - 1));
                        }
                    } else {
                        setCurrentSlide(Math.min(prevIndex, slides.length - 1));
                    }
                } else if (!isLanguageToggling && !isSaving && segmentChanged) {
                    setCurrentSlide(0);
                }
            }

            if (isLanguageToggling) {
                isLanguageTogglingRef.current = false;
            }
            if (isSaving) {
                isSavingRef.current = false;
            }
            lastActiveSegmentRef.current = activeSegment;
        } else {
            setCurrentSegmentSlides([]);
            lastActiveSegmentRef.current = activeSegment;
        }
    }, [activeSegment, sessionSlides, sessionAssessments, selectedSession, lastSpokenSlideTitleRef]);

    // Helper to find next session
    const findNextSession = useCallback(() => {
        if (!selectedSession || !modulesData || modulesData.length === 0) {
            return null;
        }

        const currentSessionCode = selectedSession.session_code;
        let foundCurrent = false;
        let nextSession = null;

        for (const module of modulesData) {
            if (!module.units) continue;
            for (const unit of module.units) {
                if (!unit.lessons) continue;
                for (const session of unit.lessons) {
                    if (foundCurrent) {
                        nextSession = session;
                        break;
                    }
                    if (session.session_code === currentSessionCode) {
                        foundCurrent = true;
                    }
                }
                if (nextSession) break;
            }
            if (nextSession) break;
        }

        return nextSession;
    }, [selectedSession, modulesData]);

    // Helper to find previous session
    const findPreviousSession = useCallback(() => {
        if (!selectedSession || !modulesData || modulesData.length === 0) {
            return null;
        }

        const currentSessionCode = selectedSession.session_code;
        let previousSession = null;

        for (const module of modulesData) {
            if (!module.units) continue;
            for (const unit of module.units) {
                if (!unit.lessons) continue;
                for (const session of unit.lessons) {
                    if (session.session_code === currentSessionCode) {
                        return previousSession;
                    }
                    previousSession = session;
                }
            }
        }

        return null;
    }, [selectedSession, modulesData]);

    // Expand module and unit for session
    const expandSessionInSidebar = useCallback((session) => {
        if (!session || !modulesData || modulesData.length === 0) return;

        let moduleIndex = -1;
        let unitIndex = -1;

        for (let mIdx = 0; mIdx < modulesData.length; mIdx++) {
            const module = modulesData[mIdx];
            if (!module.units) continue;

            for (let uIdx = 0; uIdx < module.units.length; uIdx++) {
                const unit = module.units[uIdx];
                if (!unit.lessons) continue;

                const foundSession = unit.lessons.find(s => s.session_code === session.session_code);
                if (foundSession) {
                    moduleIndex = mIdx;
                    unitIndex = uIdx;
                    break;
                }
            }
            if (moduleIndex !== -1) break;
        }

        if (moduleIndex !== -1 && setOpenModules) {
            setOpenModules(prev => ({
                ...prev,
                [moduleIndex]: true
            }));
        }

        if (moduleIndex !== -1 && unitIndex !== -1 && setOpenUnits) {
            setOpenUnits(prev => ({
                ...prev,
                [`${moduleIndex}-${unitIndex}`]: true
            }));
        }

        const sessionCode = session.session_code;
        if (setOpenSessions) {
            const newOpenSessions = {};
            Object.keys(openSessions).forEach(key => { newOpenSessions[key] = false; });
            newOpenSessions[sessionCode] = true;
            setOpenSessions(newOpenSessions);
        }

        if (!isMobile && isCollapsed && setIsCollapsed) {
            setIsCollapsed(false);
        }
    }, [modulesData, openSessions, setOpenModules, setOpenUnits, setOpenSessions, isMobile, isCollapsed, setIsCollapsed]);

    const hasNextSession = useMemo(() => {
        const nextSession = findNextSession();
        if (!nextSession) return false;
        const nextSessionStatusData = contentStatus[nextSession.session_code];
        const nextSessionStatus = typeof nextSessionStatusData === 'object' ? nextSessionStatusData.status : nextSessionStatusData;
        return nextSessionStatus === 'done';
    }, [findNextSession, contentStatus]);

    const hasPreviousSession = useMemo(() => {
        const previousSession = findPreviousSession();
        if (!previousSession) return false;
        const prevSessionStatusData = contentStatus[previousSession.session_code];
        const prevSessionStatus = typeof prevSessionStatusData === 'object' ? prevSessionStatusData.status : prevSessionStatusData;
        return prevSessionStatus === 'done';
    }, [findPreviousSession, contentStatus]);

    const handleNextSegment = useCallback(() => {
        if (segments.length === 0) return;

        const currentIndex = segments.findIndex(seg => seg.id === activeSegment);

        if (currentIndex < segments.length - 1) {
            const nextSegment = segments[currentIndex + 1];
            setActiveSegment(nextSegment.id);
            setCurrentSlide(0);
            setCurrentSegment(currentIndex + 1);
        } else if (currentIndex === segments.length - 1) {
            const nextSession = findNextSession();
            if (nextSession) {
                const nextSessionStatusData = contentStatus[nextSession.session_code];
                const nextSessionStatus = typeof nextSessionStatusData === 'object' ? nextSessionStatusData.status : nextSessionStatusData;
                if (nextSessionStatus === 'done') {
                    expandSessionInSidebar(nextSession);
                    fetchSessionSlides(nextSession);
                }
            }
        }
    }, [segments, activeSegment, findNextSession, contentStatus, expandSessionInSidebar, fetchSessionSlides]);

    const handlePreviousSegment = useCallback(() => {
        if (segments.length === 0) return;

        const currentIndex = segments.findIndex(seg => seg.id === activeSegment);

        if (currentIndex > 0) {
            const prevSegment = segments[currentIndex - 1];
            landOnLastSlideRef.current = true;
            setActiveSegment(prevSegment.id);
            setCurrentSegment(currentIndex - 1);
        } else if (currentIndex === 0) {
            const previousSession = findPreviousSession();
            if (previousSession) {
                const prevSessionStatusData = contentStatus[previousSession.session_code];
                const prevSessionStatus = typeof prevSessionStatusData === 'object' ? prevSessionStatusData.status : prevSessionStatusData;
                if (prevSessionStatus === 'done') {
                    expandSessionInSidebar(previousSession);
                    fetchSessionSlides(previousSession, true);
                }
            }
        }
    }, [segments, activeSegment, findPreviousSession, contentStatus, expandSessionInSidebar, fetchSessionSlides]);

    const handleNextSlide = useCallback(() => {
        if (currentSlide < currentSegmentSlides.length - 1) {
            setCurrentSlide(prev => prev + 1);
        } else if (currentSegment < segments.length - 1 || hasNextSession) {
            handleNextSegment();
        }
    }, [currentSlide, currentSegmentSlides.length, currentSegment, segments.length, hasNextSession, handleNextSegment]);

    const handlePrevSlide = useCallback(() => {
        if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
        } else {
            handlePreviousSegment();
        }
    }, [currentSlide, handlePreviousSegment]);

    const handleViewSegment = useCallback((session, segmentId) => {
        if (setShowOutline) setShowOutline(false);
        setSelectedSession(session);
        if (setSelectedSection) setSelectedSection('Session Details');
        setActiveSegment(segmentId);
        setCurrentSlide(0);

        const segmentIndex = segments.findIndex(seg => seg.id === segmentId);
        if (segmentIndex !== -1) {
            setCurrentSegment(segmentIndex);
        }
    }, [setShowOutline, setSelectedSession, setSelectedSection, segments]);

    const isSegmentActive = useCallback((segmentId) => {
        return activeSegment === segmentId;
    }, [activeSegment]);

    const showNextSegmentButton = currentSlide === currentSegmentSlides.length - 1 && (currentSegment < segments.length - 1 || hasNextSession);
    const showPrevSegmentButton = currentSlide === 0 && (currentSegment > 0 || hasPreviousSession);

    // Keyboard navigation hotkeys (Left Arrow / Right Arrow / Space)
    useEffect(() => {
        if (!enabled || isEditMode) return;

        const handleKeyDown = (e) => {
            // Ignore if active element is an editable target (input, textarea, select, contenteditable)
            const activeEl = document.activeElement;
            if (
                activeEl &&
                (activeEl.tagName === 'INPUT' ||
                 activeEl.tagName === 'TEXTAREA' ||
                 activeEl.tagName === 'SELECT' ||
                 activeEl.isContentEditable)
            ) {
                return;
            }

            if (e.key === 'ArrowRight' || e.key === 'Space' || e.key === ' ') {
                e.preventDefault();
                handleNextSlide();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                handlePrevSlide();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [enabled, isEditMode, handleNextSlide, handlePrevSlide]);

    return {
        currentSlide,
        setCurrentSlide,
        currentSegment,
        setCurrentSegment,
        activeSegment,
        setActiveSegment,
        currentSegmentSlides,
        setCurrentSegmentSlides,
        segments,
        setSegments,
        currentSessionSlide,
        currentSlideRef,
        landOnLastSlideRef,
        preserveSlideIdRef,
        preserveSlideIndexRef,
        isLanguageTogglingRef,
        isSavingRef,
        findNextSession,
        findPreviousSession,
        hasNextSession,
        hasPreviousSession,
        expandSessionInSidebar,
        handleNextSlide,
        handlePrevSlide,
        handleNextSegment,
        handlePreviousSegment,
        handleViewSegment,
        isSegmentActive,
        showNextSegmentButton,
        showPrevSegmentButton,
        pollResponses,
        setPollResponses,
        recordPollSubmission,
        currentPollFeedback,
        isPollCorrect,
    };
};

export default useSlideNavigation;
